<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { authenticated, canAccess, logout } from "@/stores/admin.js";
import { permissions } from "@/domain/labels.js";
import { notification, dialog, answerDialog } from "@/utils/feedback.js";
const route = useRoute(),
  router = useRouter(),
  pageView = ref(null),
  menuOpen = ref(false);
const brand = document.title || "管理后台";
const navigation = computed(() =>
  permissions.filter((item) => canAccess(item.key)),
);
const title = computed(
  () =>
    permissions.find((item) => item.key === route.meta.permission)?.label ||
    route.meta.title ||
    "",
);
const isShell = computed(() => authenticated.value && route.path !== "/login");
function closeMenu() {
  menuOpen.value = false;
}
function keydown(event) {
  if (event.key === "Escape") {
    closeMenu();
    if (dialog.value) answerDialog(false);
  }
}
function resize() {
  if (innerWidth > 900) closeMenu();
}
function signOut() {
  logout();
  router.replace("/login");
}
watch(menuOpen, (open) =>
  document.body.classList.toggle("admin-menu-open", open),
);
watch(() => route.path, closeMenu);
window.addEventListener("keydown", keydown);
window.addEventListener("resize", resize);
onBeforeUnmount(() => {
  window.removeEventListener("keydown", keydown);
  window.removeEventListener("resize", resize);
  document.body.classList.remove("admin-menu-open");
});
</script>
<template>
  <div v-if="isShell" class="shell">
    <aside id="admin-sidebar">
      <div class="brand" :data-brand-mark="brand.charAt(0)">{{ brand }}</div>
      <button
        v-for="item in navigation"
        :key="item.key"
        :class="{ active: route.meta.permission === item.key }"
        @click="router.push('/' + item.key)"
      >
        {{ item.label }}</button
      ><button class="logout" @click="signOut">退出登录</button>
    </aside>
    <main>
      <header>
        <h1>{{ title }}</h1>
        <button @click="pageView?.refresh?.()">刷新</button>
      </header>
      <RouterView v-slot="{ Component }"
        ><component :is="Component" :key="route.path" ref="pageView"
      /></RouterView>
    </main>
  </div>
  <RouterView v-else />
  <template v-if="isShell"
    ><button
      id="admin-mobile-menu-toggle"
      type="button"
      aria-label="打开后台菜单"
      aria-controls="admin-sidebar"
      :aria-expanded="menuOpen"
      @click="menuOpen = !menuOpen"
    >
      ☰</button
    ><button
      id="admin-mobile-menu-backdrop"
      type="button"
      aria-label="关闭后台菜单"
      @click="closeMenu"
  /></template>
  <div v-if="notification" class="admin-notification" role="status">
    {{ notification }}
  </div>
  <div v-if="dialog" class="admin-dialog-mask">
    <form
      class="admin-dialog"
      role="dialog"
      aria-modal="true"
      :aria-label="dialog.title"
      @submit.prevent="answerDialog(true)"
    >
      <h2>{{ dialog.title }}</h2>
      <p>{{ dialog.message }}</p>
      <input
        v-if="dialog.kind === 'prompt'"
        v-model="dialog.value"
        :type="dialog.password ? 'password' : 'text'"
        :aria-label="dialog.message"
        autofocus
      />
      <div class="form-actions">
        <button>{{ dialog.confirmText }}</button
        ><button type="button" class="ghost" @click="answerDialog(false)">
          {{ dialog.cancelText }}
        </button>
      </div>
    </form>
  </div>
</template>
<style>
.admin-notification {
  position: fixed;
  z-index: 99999;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%);
  max-width: 90vw;
  padding: 12px 22px;
  border-radius: 10px;
  background: #232327ed;
  color: white;
  box-shadow: 0 6px 28px #0002;
}
.admin-dialog-mask {
  position: fixed;
  inset: 0;
  background: #0007;
  z-index: 99998;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.admin-dialog {
  background: white;
  border-radius: 14px;
  padding: 24px;
  width: 460px;
  max-width: 100%;
  box-shadow: 0 12px 60px #0003;
}
.admin-dialog h2 {
  margin: 0 0 16px;
  font-size: 19px;
}
.admin-dialog p {
  white-space: pre-wrap;
  line-height: 1.65;
  overflow-wrap: anywhere;
}
.admin-dialog input {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 20px;
}
.admin-dialog .form-actions {
  justify-content: flex-end;
}
button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
.table-scroll {
  width: 100%;
  overflow-x: auto;
}
</style>
